import { useState } from "react"
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from "lucide-react"
import { useGetCouponsQuery, useCreateCouponMutation, useUpdateCouponMutation, useDeleteCouponMutation, type AdminCoupon } from "../features/admin/adminApi"

type FormState = {
  code: string; name: string; description: string
  discountType: "percentage" | "fixed"; discountValue: string
  minOrderAmount: string; maxDiscountAmount: string
  validFrom: string; validTo: string; usageLimit: string; maxPerUser: string
}

const EMPTY: FormState = {
  code: "", name: "", description: "", discountType: "percentage", discountValue: "",
  minOrderAmount: "0", maxDiscountAmount: "0", validFrom: "", validTo: "", usageLimit: "", maxPerUser: "1",
}

export default function CouponsPage() {
  const { data: coupons = [], isLoading } = useGetCouponsQuery()
  const [createCoupon] = useCreateCouponMutation()
  const [updateCoupon] = useUpdateCouponMutation()
  const [deleteCoupon] = useDeleteCouponMutation()

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<AdminCoupon | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)

  const fmt = (d: string) => d ? new Date(d).toISOString().split("T")[0] : ""

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true) }
  const openEdit = (c: AdminCoupon) => {
    setEditing(c)
    setForm({
      code: c.code, name: c.name, description: c.description ?? "",
      discountType: c.discountType as any, discountValue: String(c.discountValue),
      minOrderAmount: String(c.minOrderAmount), maxDiscountAmount: String(c.maxDiscountAmount),
      validFrom: fmt(c.validFrom), validTo: fmt(c.validTo),
      usageLimit: String(c.usageLimit), maxPerUser: String(c.maxPerUser),
    })
    setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditing(null) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const base = {
      name: form.name, description: form.description || undefined,
      discountValue: Number(form.discountValue),
      minOrderAmount: Number(form.minOrderAmount),
      maxDiscountAmount: Number(form.maxDiscountAmount),
      validFrom: form.validFrom, validTo: form.validTo,
      usageLimit: Number(form.usageLimit), maxPerUser: Number(form.maxPerUser),
    }
    if (editing) {
      await updateCoupon({ uid: editing.id, ...base })
    } else {
      await createCoupon({ code: form.code, discountType: form.discountType, ...base })
    }
    closeForm()
  }

  const handleToggle = async (c: AdminCoupon) => {
    await updateCoupon({ uid: c.id, isActive: !c.isActive })
  }

  const handleDelete = async (uid: string) => {
    if (!window.confirm("Delete this coupon?")) return
    await deleteCoupon(uid)
  }

  const field = (label: string, key: keyof FormState, type = "text", extra?: Partial<React.InputHTMLAttributes<HTMLInputElement>>) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" {...extra} />
    </div>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
          <Plus size={16} /> New Coupon
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 overflow-y-auto max-h-[90vh]">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{editing ? "Edit Coupon" : "New Coupon"}</h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {!editing && field("Code *", "code", "text", { required: true, placeholder: "e.g. SAVE20" })}
                {field("Name *", "name", "text", { required: true })}
              </div>
              {field("Description", "description")}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type *</label>
                  <select value={form.discountType} onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                {field("Discount Value *", "discountValue", "number", { required: true, min: "0", step: "0.01" })}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field("Min Order Amount", "minOrderAmount", "number", { min: "0", step: "0.01" })}
                {field("Max Discount Amount", "maxDiscountAmount", "number", { min: "0", step: "0.01" })}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field("Valid From *", "validFrom", "date", { required: true })}
                {field("Valid To *", "validTo", "date", { required: true })}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field("Usage Limit *", "usageLimit", "number", { required: true, min: "1" })}
                {field("Max Per User", "maxPerUser", "number", { min: "1" })}
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">{editing ? "Save" : "Create"}</button>
                <button type="button" onClick={closeForm} className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading coupons...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Code</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Discount</th>
                  <th className="px-4 py-3 text-left">Validity</th>
                  <th className="px-4 py-3 text-left">Usage</th>
                  <th className="px-4 py-3 text-left">Active</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coupons.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No coupons yet. Click "New Coupon" to create one.</td></tr>
                ) : coupons.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-bold text-gray-900">{c.code}</td>
                    <td className="px-4 py-3 text-gray-700">{c.name}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.discountType === "percentage" ? `${c.discountValue}%` : `₦${Number(c.discountValue).toLocaleString()}`}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(c.validFrom).toLocaleDateString()} – {new Date(c.validTo).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{c.usageLimit} (max {c.maxPerUser}/user)</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleToggle(c)} className={c.isActive ? "text-green-600" : "text-gray-400"}>
                        {c.isActive ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 flex gap-1">
                      <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Pencil size={15} /></button>
                      <button onClick={() => handleDelete(c.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
